import api from '@/helpers/api_helper';
import { apiUrls } from '@/constant/configure';
import qs from 'qs';
import delay from '@/utils/delay';
import { getOwnMalls } from './auth.service';
import { getBusinessInfo } from './mall.service';

const StudioApiBase = apiUrls.studio;
interface getPartnersProductListPropType {
    user_id: string;
    block_id?: string;
    keyword?: string;
    category_id?: string;
    category_tag?: string;
    offset?: number;
    limit?: number;
}
export const getPartnersProductList = async (props: getPartnersProductListPropType) => {
    const { user_id, ...rest } = props;
    const query = {
        ...rest,
        offset: rest?.offset || 0,
        limit: rest?.limit || 10,
    };
    const queryString = `${qs.stringify(query)}`;
    try {
        return await api
            .get(`/v2/spacehome/partners-products${queryString ? `?${queryString}` : ''}`, { baseURL: StudioApiBase })
            .then((r) => {
                return {
                    list:
                        (r.data?.item_list as {
                            product_id: string;
                            product_name: string;
                            product_image_url: string;
                            start_date_time: string;
                            end_date_time: string;
                            mall_id: string;
                            price_info: {
                                product_price: number;
                                discount_method: string;
                                discount_value: number;
                                product_charge: number;
                            };
                            matching_product_group_id: string;
                            isGroupPurchaseAllowed: boolean;
                            isPickSale: boolean;
                            sale_commission: string;
                        }[]) || [],
                    total: (r.data?.total_count as number) || 0,
                };
            });
    } catch (error) {
        throw error;
    }
};

export const getPartnersProductListWithBlockId = async ({
    user_id,
    block_id,
}: {
    user_id: string;
    block_id: string;
}) => {
    try {
        return await api
            .get(`/v2/spacehome/${user_id}/event-blocks/${block_id}/partners-blocks/products`, {
                baseURL: StudioApiBase,
            })
            .then(
                (r) =>
                    (r.data?.item_list as {
                        product_id: string;
                        product_name: string;
                        product_image_url: string;
                        start_date_time: string;
                        end_date_time: string;
                    }[]) || [],
            );
    } catch (error) {
        throw error;
    }
};
interface postPartnersBlockPropType {
    user_id: string;
    data: {
        product_ids: string[];
        is_block_bundle: boolean;
    };
}
export const postPartnersBlock = async ({ user_id, data }: postPartnersBlockPropType) => {
    try {
        return await api.post(`/v2/spacehome/${user_id}/partners-blocks`, { ...data }, { baseURL: StudioApiBase }).then(
            (r) =>
                (r.data?.item_list as {
                    block_id: string;
                    block_order_id: number;
                    block_type: string;
                    block_content: {
                        title: string;
                        sub_title: string;
                        is_activated: boolean;
                        on_off: boolean;
                        is_valid: boolean;
                        partners_content: {
                            partners_id: string;
                            order_id: number;
                            title: string;
                            img_url: string;
                            link_url: string;
                            start_date_time: string;
                            end_date_time: string;
                            created_at: string;
                            on_off: boolean;
                            is_valid: boolean;
                            is_activated: boolean;
                        }[];
                    };
                }[]) || [],
        );
    } catch (error) {
        throw error;
    }
};
interface putPartnersBlockProductPropType {
    user_id: string;
    block_id: string;
    data: {
        product_ids: string[];
    };
}
export const putPartnersBlockProduct = async ({ user_id, block_id, data }: putPartnersBlockProductPropType) => {
    try {
        return await api
            .put(
                `/v2/spacehome/${user_id}/event-blocks/${block_id}/partners-blocks/products`,
                { ...data },
                { baseURL: StudioApiBase },
            )
            .then((r) => r.data?.item || {});
    } catch (error) {
        throw error;
    }
};
interface patchPartnersBlockPropType {
    user_id: string;
    block_id: string;
    block_content: {
        title?: string;
        sub_title?: string;
        partners_block_id_list?: string[];
    };
}
export const patchPartnersBlock = async ({ user_id, block_id, block_content }: patchPartnersBlockPropType) => {
    try {
        return await api
            .patch(
                `/v2/spacehome/${user_id}/event-blocks/${block_id}/partners-blocks`,
                { block_content },
                { baseURL: StudioApiBase },
            )
            .then((r) => r.data?.item || {});
    } catch (error) {
        throw error;
    }
};
interface patchPartnersBlockProductPropType {
    user_id: string;
    block_id: string;
    partners_block_id: string;
    block_content: {
        on_off?: boolean;
        categories?: string[];
    };
}
export const patchPartnersBlockProduct = async ({
    user_id,
    block_id,
    partners_block_id,
    block_content,
}: patchPartnersBlockProductPropType) => {
    try {
        return await api
            .patch(
                `/v2/spacehome/${user_id}/event-blocks/${block_id}/partners-blocks/${partners_block_id}`,
                { block_content },
                { baseURL: StudioApiBase },
            )
            .then((r) => r.data);
    } catch (error) {
        throw error;
    }
};
interface deletePartnersBlockProductPropType {
    user_id: string;
    block_id: string;
    partners_block_id: string;
}
export const deletePartnersBlockProduct = async ({
    user_id,
    block_id,
    partners_block_id,
}: deletePartnersBlockProductPropType) => {
    try {
        return await api
            .delete(`/v2/spacehome/${user_id}/event-blocks/${block_id}/partners-blocks/${partners_block_id}`, {
                baseURL: StudioApiBase,
            })
            .then((r) => r.data);
    } catch (error) {
        throw error;
    }
};
export const getPartnersRevenue = async ({
    user_id,
    offset = 0,
    limit = 10,
}: {
    user_id: string;
    offset?: number;
    limit?: number;
}) => {
    try {
        const queryString = qs.stringify({ offset, limit });
        return await api
            .get(`/v2/spacehome/${user_id}/partners/revenue?${queryString}`, { baseURL: StudioApiBase })
            .then(
                (r) =>
                    r.data?.item as {
                        overall_statistics: {
                            total_click: number;
                            total_purchase: number;
                            total_purchase_amount: number;
                            total_refund_amount: number;
                            total_revenue_amount: number;
                            total_conversion_rate: number;
                        };
                        product_list: {
                            product_id: string;
                            product_name: string;
                            product_image: string;
                            product_statistics: {
                                click_count: number;
                                purchase_count: number;
                                purchase_amount: number;
                                refund_amount: number;
                                revenue_amount: number;
                                conversion_rate: number;
                            };
                        }[];
                        total_count: number;
                        last_updated_at: string;
                    },
            );
    } catch (error) {
        throw error;
    }
};
export const getPartnersRevenueDetail = async ({
    user_id,
    product_id = '',
    offset = 0,
    limit = 10,
}: {
    user_id: string;
    product_id?: string;
    offset?: number;
    limit?: number;
}) => {
    try {
        const queryString = qs.stringify({ offset, limit, product_id });
        return await api
            .get(`/v2/spacehome/${user_id}/partners/revenue/product?${queryString}`, { baseURL: StudioApiBase })
            .then((r) => r.data?.item || {});
    } catch (error) {
        throw error;
    }
};
export const getUserMallBusinessInfo = async () => {
    try {
        const { ownMallList } = await getOwnMalls().catch(() => {
            return { ownMallList: [] };
        });
        if (ownMallList.length === 0) return {};
        const mallId = ownMallList[0]?.mallId || '';
        return await getBusinessInfo(mallId);
    } catch (error) {
        throw error;
    }
};
interface postAuthAccountPropType {
    userId: string;
    payload: {
        settleBankCode: string; // 은행코드
        settleBank: string; // 은행명
        settleAccountNumber: string; // 계좌번호
        settleAccountOwnerName: string; // 예금주명
    };
}
export const postAuthAccount = async ({ userId, payload }: postAuthAccountPropType) => {
    try {
        return await api
            .post(`/v2/spacehome/${userId}/account/verify`, { ...payload }, { baseURL: StudioApiBase })
            .then((r) => r?.data || false);
    } catch (error) {
        throw error;
    }
};
export const deleteCacheClear = async ({
    type,
    userId,
}: {
    type: /** 수익 조회 */
    | 'REVENUE'
        /** 수익 상세 조회 */
        | 'REVENUE_DETAIL';
    userId: string;
}) => {
    try {
        let url: string;
        switch (type) {
            case 'REVENUE':
                url = `${StudioApiBase}/v2/spacehome/${userId}/partners/revenue`;
                break;
            case 'REVENUE_DETAIL':
                url = `${StudioApiBase}/v2/spacehome/${userId}/partners/revenue/product?`;
                break;
        }

        await api.delete(`/v2/spacehome/cache/clear?url=${url}`, { baseURL: StudioApiBase });
    } catch (error) {
        throw error;
    }
};
